import { DOCUMENT, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormErrorComponent,
  FormInputComponent,
  FormInputDirective,
  FormLabelDirective,
} from '@app/base-components';
import { AbstractContentfulBase } from '@app/contentful';
import {
  ButtonQaId,
  HabaQaId,
  postalCodeUrl,
  PostalCodeValidator,
  TextfieldQaId,
} from '@app/helpers';
import { CustomSiteContext } from '@app/services';
import { Entry } from 'contentful';
import {
  ContentfulDynamicComponentRestService,
  ContentfulRestService,
  ContentfulSeoMetadataService,
  Page,
} from 'haba-contentful-components';
import { I18nModule, PageMeta, WindowRef } from 'haba-core';
import { first, take } from 'rxjs/operators';
import {
  PostalCodeSearchResponse,
  PostalCodeService,
} from 'src/app/services/postal-code/postal.code.service';

@Component({
  selector: 'app-postal-code',
  templateUrl: './postal-code.component.html',
  styleUrls: ['./postal-code.component.scss'],
  imports: [
    NgIf,
    I18nModule,
    ReactiveFormsModule,
    FormLabelDirective,
    FormInputDirective,
    FormInputComponent,
    FormErrorComponent,
  ],
})
export class PostalCodeComponent extends AbstractContentfulBase implements OnInit, AfterViewInit {
  @ViewChild('container', { read: ViewContainerRef }) public target: ViewContainerRef;
  @ViewChild('postalCodeContainer', { static: false }) public postalCodeContainer: ElementRef;

  public entry: Entry<Page>;
  public postalCodeQaId: HabaQaId;
  public postalCodeInputQaId: TextfieldQaId;
  public postalCodeButtonQaId: ButtonQaId;
  public contactData: PostalCodeSearchResponse;
  private baseQaId: HabaQaId;
  private zone = this.router.url.split('/')[0];
  private defaultAvatar =
    'https://cdn.hff.de/image/upload/v1769781674/Assets/WEHRFRITZ/Service-Vorteile/WEHRFRITZ_Personen_Avatar.jpg';

  private submitted: boolean;
  private submittedSuccessful: boolean;

  postalCodeForm = this.fb.group({
    postalCode: ['', [PostalCodeValidator(), this.postalCodeLengthValidator()]],
  });

  constructor(
    @Inject(DOCUMENT) private document: Document,
    protected route: ActivatedRoute,
    protected router: Router,
    protected siteContextService: CustomSiteContext,
    protected contentfulRestService: ContentfulRestService,
    protected contentfulDynamicComponentRestService: ContentfulDynamicComponentRestService,
    protected cdr: ChangeDetectorRef,
    protected contentfulSeoMetadataService: ContentfulSeoMetadataService,
    protected winRef: WindowRef,
    private fb: FormBuilder,
    private renderer2: Renderer2,
    private postalCodeService: PostalCodeService
  ) {
    super(router, siteContextService);
  }

  ngOnInit() {
    this.initIds();

    const control = this.postalCodeForm.get('postalCode');
    control?.valueChanges.subscribe(() => {
      this.submitted = false;
      this.submittedSuccessful = false;
      this.contactData = null;
    });
  }

  ngAfterViewInit(): void {
    this.contentfulRestService
      .getPageBySlug(`${postalCodeUrl}`, this.locale)
      .pipe(first())
      .subscribe((entry: Entry<Page> | null) => {
        if (entry === null) {
          this.notFound();
        } else {
          this.entry = entry;
          const pageMeta: PageMeta = {
            title: this.contentfulSeoMetadataService.getTitle(entry),
            description: this.contentfulSeoMetadataService.getDescription(entry),
            image: this.contentfulSeoMetadataService.getImage(entry),
            robots: this.contentfulSeoMetadataService.getRobots(entry),
            canonicalUrl: this.contentfulSeoMetadataService.getCanonicalUrl(entry, this.winRef),
          };
          this.contentfulSeoMetadataService.setMetadata(pageMeta);
          this.contentfulDynamicComponentRestService.renderComponents(
            entry.fields.content,
            this.target
          );

          this.appendPostalCodeFormToPlaceholder();
          this.cdr.detectChanges();
        }
      });
  }

  public submitPostalCode() {
    this.submitted = true;

    if (this.postalCodeForm.invalid) {
      return;
    }

    const postalCodeValue = this.postalCodeForm.get('postalCode').value;

    this.postalCodeService
      .getContactByPostalCode(postalCodeValue)
      .pipe(take(1))
      .subscribe((data) => {
        this.submittedSuccessful = true;
        this.contactData = { ...data, image: data.image || this.defaultAvatar };
        this.postalCodeForm.get('postalCode')?.markAsPristine();
        this.postalCodeForm.get('postalCode')?.markAsUntouched();
      });
  }

  get isSubmittedAndValid(): boolean {
    return this.submittedSuccessful && this.postalCodeForm.valid;
  }

  get postalCodeHasError(): boolean {
    const control = this.postalCodeForm.get('postalCode');
    const invalidControl = !!(control && control.invalid && (control.touched || control.dirty));
    const noContactCity = this.contactData && !this.contactData.city;

    return this.submitted && (invalidControl || noContactCity);
  }

  private appendPostalCodeFormToPlaceholder() {
    const postalCodePlaceholder = this.document.getElementById('postal-code-placeholder');

    if (postalCodePlaceholder) {
      this.renderer2.appendChild(postalCodePlaceholder, this.postalCodeContainer.nativeElement);
    }
  }

  private postalCodeLengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value;

      if (!value) return null;

      const locale = this.zone as 'de-at' | 'de-de';
      const expectedLength = locale === 'de-at' ? 4 : 5;

      return value.length === expectedLength
        ? null
        : {
            postalCodeLength: {
              requiredLength: expectedLength,
              actualLength: value.length,
              locale,
            },
          };
    };
  }

  private initIds(): void {
    this.baseQaId = HabaQaId.forPage(HabaQaId.page.postalCode);
    this.postalCodeQaId = this.baseQaId.forSection(HabaQaId.section.container);
    this.postalCodeButtonQaId = this.baseQaId.getButtonId(HabaQaId.elements.button.postalCode);
  }
}
